// One product: every ledger movement with a running balance, and its label rates.
// A label rate is used in any calculation only after a person confirms it here.
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { addLabelRate, decideLabelRate, errorText, getProductLedger, getUnitCost, listLabelRates, qty, setUnitCost } from "./useChemicalsApi";
import { getUserRole } from "../../../helpers/tenantHelpers";
import "./Chemicals.css";

const TYPE_WORDS = {
  receive: "Received", consume: "Applied", consume_reversal: "Application reversed", transfer_in: "Transferred in",
  transfer_out: "Transferred out", adjust: "Adjusted up", adjust_out: "Adjusted down", return: "Returned to vendor", return_in: "Put back",
};

const REASON_WORDS = {
  visit: "Used on a visit", visit_return: "Returned from a visit", delivery: "Delivery", transfer: "Moved",
  count_correction: "Count correction", damage: "Damaged", expired: "Expired", fr_application: "FieldRoutes application", other: "Other",
};
const COST_ROLES = new Set(["owner", "admin", "manager"]);
const money4 = (v) => (v == null ? "—" : `$${Number(v).toFixed(Number(v) < 1 ? 4 : 2)}`);
const when = (v) => new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

/* Unit cost: what one of this product's units costs. The chemical-cost report multiplies use by it,
   so a product with no cost shows up there as "uncosted", never as $0. A cost can be entered in a
   compatible unit (a price per gallon on a product stocked in fl oz) and is converted. */
function UnitCost({ product }) {
  const canEdit = COST_ROLES.has(String(getUserRole("Member") || "").toLowerCase());
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ cost: "", unit: product.uom || "", note: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = useCallback(async () => {
    try { setData(await getUnitCost(product.id)); } catch (e) { setMsg({ ok: false, text: errorText(e, "Couldn't load the cost.") }); }
  }, [product.id]);
  useEffect(() => { load(); }, [load]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const out = await setUnitCost(product.id, { cost: form.cost, unit: form.unit || product.uom, note: form.note });
      setMsg({ ok: true, text: `Saved: ${money4(out.unit_cost)} per ${out.unit}.` });
      setForm((f) => ({ ...f, cost: "", note: "" }));
      load();
    } catch (err) {
      setMsg({ ok: false, text: errorText(err, "Couldn't save that cost.") });
    } finally { setBusy(false); }
  };
  return (
    <section className="chem-card">
      <div className="chem-head">
        <div>
          <h2>Unit cost</h2>
          <div className="chem-sub">What one {product.uom || "unit"} costs. Chemical cost reports multiply use by this; without it, use is shown as uncosted.</div>
        </div>
        <div className="chem-stat">
          <div className="chem-stat__val">{data?.unit_cost != null ? money4(data.unit_cost) : "Not set"}</div>
          <div className="chem-stat__label">per {product.uom || "unit"}{data?.last_set ? ` · set by ${data.last_set.set_by} on ${when(data.last_set.set_at)}` : ""}</div>
        </div>
      </div>
      {canEdit ? (
        <form className="chem-form" onSubmit={save}>
          <label>Cost ($)<input type="number" step="any" min="0" value={form.cost} onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value }))} required /></label>
          <label>Per<input value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} style={{ width: 90 }} placeholder={product.uom} /></label>
          <label>Note<input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="e.g. Univar invoice 10/02" /></label>
          <button className="chem-btn" disabled={busy}>{busy ? "Saving…" : "Save cost"}</button>
        </form>
      ) : <div className="chem-detail">A manager, admin or owner sets costs.</div>}
      {msg ? <div className={`chem-msg ${msg.ok ? "chem-msg--ok" : "chem-msg--err"}`} style={{ marginTop: "var(--s-3)" }}>{msg.text}</div> : null}
      {data?.history?.length ? (
        <div className="chem-table-wrap" style={{ marginTop: "var(--s-3)" }}>
          <table className="chem-table">
            <thead><tr><th>When</th><th className="num">Cost per {product.uom}</th><th>Entered as</th><th>By</th><th>From</th></tr></thead>
            <tbody>{data.history.map((h) => (
              <tr key={h.id}>
                <td>{when(h.set_at)}</td><td className="num">{money4(h.unit_cost)}</td><td>${h.entered}</td>
                <td>{h.set_by || "—"}</td><td className="chem-detail">{h.source === "csv" ? "CSV import" : "Typed here"}{h.note ? ` · ${h.note}` : ""}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

function LabelRates({ product }) {
  const [rates, setRates] = useState(null);
  const [form, setForm] = useState({ rate_max: "", rate_unit: product.uom || "", annual_max: "", site: "", source_span: "" });
  const [msg, setMsg] = useState("");
  const load = useCallback(async () => {
    try { setRates((await listLabelRates(product.id)).results); } catch (e) { setMsg(errorText(e, "Couldn't load label rates.")); }
  }, [product.id]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const add = async (e) => {
    e.preventDefault();
    try { await addLabelRate(product.id, { ...form, source: "manual" }); setForm((f) => ({ ...f, rate_max: "", annual_max: "", source_span: "" })); load(); }
    catch (err) { setMsg(errorText(err, "Couldn't save that rate.")); }
  };
  const decide = async (id, action) => {
    try { await decideLabelRate(id, action); load(); } catch (err) { setMsg(errorText(err, "Couldn't save that decision.")); }
  };
  return (
    <section className="chem-card">
      <h2>Label rates</h2>
      <div className="chem-sub" style={{ marginBottom: "var(--s-3)" }}>Rates per 1,000 sq ft, in the product's unit. A rate is used for compliance checks only after someone confirms it against the label.</div>
      {msg ? <div className="chem-msg chem-msg--err">{msg}</div> : null}
      {rates === null ? <div className="chem-empty">Loading…</div> : rates.length === 0 ? (
        <div className="chem-empty">No label rates yet. Until one is confirmed, this product's applications can't be checked against the label.</div>
      ) : (
        <table className="chem-table">
          <thead><tr><th>Site / target</th><th className="num">Max per 1,000 sq ft</th><th className="num">Yearly max</th><th>Label's words</th><th>Status</th><th /></tr></thead>
          <tbody>{rates.map((r) => (
            <tr key={r.id}>
              <td>{[r.site, r.target].filter(Boolean).join(" · ") || "—"}</td>
              <td className="num">{r.rate_max != null ? qty(r.rate_max, r.rate_unit) : "—"}</td>
              <td className="num">{r.annual_max != null ? qty(r.annual_max, r.annual_max_unit) : "—"}</td>
              <td><span className="chem-quote">{r.source_span || r.source}</span></td>
              <td>
                <span className={`chem-pill ${r.status === "confirmed" ? "chem-pill--ok" : r.status === "pending" ? "chem-pill--warn" : ""}`}>
                  {r.status === "pending" ? "Pending review" : r.status === "confirmed" ? `Confirmed${r.confirmed_by ? ` by ${r.confirmed_by}` : ""}` : "Rejected"}
                </span>
              </td>
              <td>{r.status === "pending" ? (
                <span className="chem-pills">
                  <button type="button" className="chem-btn" onClick={() => decide(r.id, "confirm")}>Confirm</button>
                  <button type="button" className="chem-btn" onClick={() => decide(r.id, "reject")}>Reject</button>
                </span>
              ) : null}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
      <form className="chem-form" onSubmit={add} style={{ marginTop: "var(--s-3)" }}>
        <label>Max per 1,000 sq ft<input type="number" step="any" min="0" value={form.rate_max} onChange={set("rate_max")} required /></label>
        <label>Unit<input value={form.rate_unit} onChange={set("rate_unit")} required style={{ width: 80 }} /></label>
        <label>Yearly max per 1,000 sq ft<input type="number" step="any" min="0" value={form.annual_max} onChange={set("annual_max")} /></label>
        <label>Site<input value={form.site} onChange={set("site")} placeholder="e.g. Turf" /></label>
        <label>Label's words<input value={form.source_span} onChange={set("source_span")} placeholder="Quote the label line" /></label>
        <button className="chem-btn">Add (pending review)</button>
      </form>
    </section>
  );
}

export default function ProductLedgerPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    getProductLedger(id).then(setData).catch((e) => setError(errorText(e, "Couldn't load this product.")));
  }, [id]);

  if (error) return <div className="chem-page"><div className="chem-msg chem-msg--err">{error}</div></div>;
  if (!data) return <div className="chem-page"><div className="chem-empty">Loading…</div></div>;
  const p = data.product;
  return (
    <div className="chem-page">
      <div className="chem-head">
        <div>
          <Link to="/operations/inventory/on-hand" className="chem-detail">← On hand</Link>
          <h1>{p.name}</h1>
          <div className="chem-sub">{p.epa_reg_number ? `EPA ${p.epa_reg_number} · ` : ""}Stocked in {p.uom}</div>
        </div>
        <div className="chem-stat"><div className={`chem-stat__val ${data.on_hand < 0 ? "chem-neg" : ""}`}>{qty(data.on_hand, p.uom)}</div><div className="chem-stat__label">On hand, all locations</div></div>
      </div>
      <UnitCost product={p} />
      <LabelRates product={p} />
      <section className="chem-card">
        <h2>Ledger</h2>
        {data.results.length === 0 ? <div className="chem-empty">No movements yet.</div> : (
          <div className="chem-table-wrap">
            <table className="chem-table">
              <thead><tr><th>When</th><th>Who</th><th>Movement</th><th>From → to</th><th className="num">Change</th><th className="num">Balance</th><th>Reason</th><th>Visit</th><th>Receipt / photo</th><th>Note</th></tr></thead>
              <tbody>{data.results.map((r) => (
                <tr key={r.id}>
                  <td>{new Date(r.when).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</td>
                  <td>{r.by || <span className="chem-detail">system</span>}</td>
                  <td>{TYPE_WORDS[r.type] || r.type}</td>
                  <td>{[r.from, r.to].filter(Boolean).join(" → ") || r.location}</td>
                  <td className={`num ${r.qty < 0 ? "chem-neg" : ""}`}>{r.qty > 0 ? "+" : ""}{qty(r.qty, r.uom)}</td>
                  <td className={`num ${r.balance < 0 ? "chem-neg" : ""}`}>{qty(r.balance, p.uom)}</td>
                  <td>{REASON_WORDS[r.reason] || r.reason || "—"}</td>
                  <td>{r.appointment ? `${r.appointment.customer || "Appointment"}` : r.fieldroutes_appointment_id ? `FieldRoutes ${r.fieldroutes_appointment_id}` : "—"}</td>
                  <td>{r.photo?.url ? <a href={r.photo.url} target="_blank" rel="noreferrer">{r.photo.name || "open"}</a> : r.photo ? "attached" : "—"}</td>
                  <td className="chem-detail">{r.note}</td>
                </tr>
              ))}</tbody>
            </table>
            {data.count > data.results.length ? <div className="chem-detail">Showing the latest {data.results.length} of {data.count}.</div> : null}
          </div>
        )}
      </section>
    </div>
  );
}
